import React, { useEffect } from 'react';
import { X, Check, ArrowUpRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Slide-in detail panel for a single capability.
 * Content is passed in via the `detail` prop (from mock.capabilityDetails).
 */
export default function CapabilityDetailModal({ open, onClose, detail, capabilityTitle }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || !detail) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-stretch justify-end bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="capability-detail-backdrop"
    >
      <div
        className="relative w-full max-w-2xl bg-[#0A1024] border-l border-white/10 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="capability-detail-modal"
      >
        <div
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.25), transparent 70%)' }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.2), transparent 70%)' }}
        />

        <div className="relative">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0A1024]/95 backdrop-blur-md border-b border-white/10 px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4]">
              <Sparkles className="w-3.5 h-3.5" />
              <span>{detail.eyebrow}</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="capability-detail-close"
              className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white/90 hover:bg-white/10"
              aria-label="Close detail"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 lg:px-8 py-8 lg:py-10">
            {capabilityTitle && (
              <div className="text-[11px] font-mono uppercase tracking-widest text-white/40 mb-3">
                Capability
              </div>
            )}
            {capabilityTitle && (
              <div className="text-2xl text-[#F1EDE4] font-medium mb-6">{capabilityTitle}</div>
            )}

            {/* Pull quote / headline */}
            <div className="relative bg-gradient-to-br from-[#4ECDC4]/10 to-transparent border border-[#4ECDC4]/25 rounded-2xl p-6 lg:p-7 mb-6">
              <div className="text-5xl text-[#4ECDC4] font-serif italic leading-none mb-2">&ldquo;</div>
              <div className="text-2xl lg:text-3xl font-serif italic text-[#F1EDE4] leading-tight">
                {detail.headline}
              </div>
            </div>

            {/* Lead paragraph */}
            <p className="text-[15px] lg:text-base text-white/75 leading-relaxed mb-8">
              {detail.lead}
            </p>

            {/* Features */}
            <div className="space-y-3">
              {detail.features.map((f, i) => (
                <div
                  key={f.title}
                  className="bg-white/[0.03] border border-white/10 rounded-xl p-4 lg:p-5 hover:border-[#4ECDC4]/40 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#4ECDC4]/15 border border-[#4ECDC4]/30 flex items-center justify-center text-[#4ECDC4] shrink-0">
                      <Check className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                        <span className="text-[9px] font-mono text-white/30">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <h4 className="text-[15px] text-[#F1EDE4] font-medium">{f.title}</h4>
                      </div>
                      <p className="text-[13px] text-white/65 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="text-[13px] text-white/60 max-w-md">
                Ready to see how this fits your pipeline goals? Book a 30-min walkthrough.
              </div>
              <Link
                to="/#contact"
                onClick={onClose}
                className="inline-flex items-center gap-2 bg-[#4ECDC4] text-[#0F1C40] pl-5 pr-4 py-3 rounded-full text-sm font-semibold hover:bg-[#FFB020] transition-colors shrink-0"
              >
                Book a strategy call
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
